import dayjs from 'dayjs';

export const TIME_UNITS = {
   SECOND: 1000,
   MINUTE: 60 * 1000,
   HOUR: 60 * 60 * 1000,
   DAY: 24 * 60 * 60 * 1000,
} as const;

export const calculateTimePercentage = (
   expiresAt: string | Date | null | undefined,
   createdAt?: string | Date | null | undefined,
): number => {
   if (!expiresAt) return 0;

   const now = Date.now();
   const expiresAtMs = dayjs(expiresAt).valueOf();

   if (Number.isNaN(expiresAtMs)) return 0;

   const createdAtMs =
      createdAt && dayjs(createdAt).isValid()
         ? dayjs(createdAt).valueOf()
         : now - TIME_UNITS.DAY * 30;

   const totalTime = expiresAtMs - createdAtMs;
   const timeLeft = expiresAtMs - now;

   if (totalTime <= 0) return 0;

   const percentage = (timeLeft / totalTime) * 100;
   const result = Math.max(0, Math.min(100, percentage));

   return Number.isNaN(result) ? 0 : result;
};

export const getTimeBackgroundColor = (percentage: number) => {
   if (percentage === 0) {
      return {
         backgroundColor: 'rgba(144, 144, 144, 0.2)',
         color: '#909090',
         borderColor: 'rgba(144, 144, 144, 0.3)',
      };
   } else if (percentage > 30) {
      return {
         backgroundColor: 'rgba(64, 133, 56, 0.2)',
         color: '#408538',
         borderColor: 'rgba(64, 133, 56, 0.3)',
      };
   } else if (percentage > 5) {
      return {
         backgroundColor: 'rgba(255, 190, 11, 0.2)',
         color: '#FFBE0B',
         borderColor: 'rgba(255, 190, 11, 0.3)',
      };
   } else {
      return {
         backgroundColor: 'rgba(207, 42, 42, 0.2)',
         color: '#CF2A2A',
         borderColor: 'rgba(207, 42, 42, 0.3)',
      };
   }
};

export const getCompletionTimeBackgroundColor = (
   totalSeconds: number,
): string => {
   const minutes = totalSeconds / 60;

   if (minutes < 5) {
      return 'bg-green-500';
   } else if (minutes < 15) {
      return 'bg-orange-500';
   } else {
      return 'bg-red-500';
   }
};

export const calculateTimeLeft = (expiresAt: string | Date): number => {
   return dayjs(expiresAt).valueOf() - Date.now();
};

export const isExpired = (expiresAt: string | Date): boolean => {
   return calculateTimeLeft(expiresAt) <= 0;
};

export const formatTime = (
   totalSeconds: number,
): { value: string; unit: string }[] => {
   const hours = Math.floor(totalSeconds / 3600);
   const minutes = Math.floor((totalSeconds % 3600) / 60);
   const seconds = totalSeconds % 60;

   const result: { value: string; unit: string }[] = [];

   if (hours > 0) {
      result.push({ value: hours.toString().padStart(2, '0'), unit: 'Heures' });
   }

   result.push({ value: minutes.toString().padStart(2, '0'), unit: 'Minutes' });
   result.push({
      value: seconds.toString().padStart(2, '0'),
      unit: 'Secondes',
   });

   return result;
};
